import React from 'react'
import { connect } from 'react-redux'
import { noop } from 'lodash-es'
import { ModalConsumerProps, NotificationType, withModal } from 'view/shared/modal'
import { ActionIcon, TicketFooter } from 'view/shared/ticket'
import { ButtonStrip, TextButton, TextButtonType } from 'view/components/buttons'
import { DialogRow } from 'view/components/inputs'
import { AuditDetail, NegotiationActions, NegotiationDetail } from 'api'
import {
  canAccept,
  canBackToStaging,
  canCancel,
  canRefresh,
  canReject,
  canSend,
  canWithdraw,
  isAuditDetail,
  isTerminalStatus
} from '../shared/utils'
import { Locale, Translation } from 'locale'
import * as constants from 'view/styles/constants'
import { iconSet } from 'view/components/icons'
import { serverClientTimeOffsetSelector } from 'selectors/timeSelectors'
import { createStructuredSelector } from 'reselect'
import { getValidUntilTime } from 'utils'

export interface RFQTicketFooterProps extends ModalConsumerProps {
  negotiationDetail: NegotiationDetail | AuditDetail
  showCloseButton?: boolean
  serverClientTimeOffset: number
  onClose?: () => unknown
  onCancel: (negotiationId: string, ownOrder: boolean) => unknown
  onReject: (negotiationId: string, ownOrder: boolean) => unknown
  onAccept: (negotiationId: string, ownOrder: boolean) => unknown
  onWithdraw: (negotiationId: string, ownOrder: boolean) => unknown
  onQuote: (negotiationId: string, ownOrder: boolean, otwDurationMins: string | null, isLastLook: boolean) => unknown
  onCounter: (negotiationId: string, ownOrder: boolean, refresh: boolean) => unknown
  onBackToStaging: (negotiationId: string, ownOrder: boolean) => unknown
}

const mapDispatchToProps = {
  onCancel: NegotiationActions.cancel,
  onReject: NegotiationActions.reject,
  onAccept: NegotiationActions.accept,
  onWithdraw: NegotiationActions.withdraw,
  onQuote: NegotiationActions.quote,
  onCounter: NegotiationActions.counter,
  onBackToStaging: NegotiationActions.backToStaging
}

const mapStateToProps = createStructuredSelector({
  serverClientTimeOffset: serverClientTimeOffsetSelector
})

export class RFQTicketFooterComponent extends React.Component<RFQTicketFooterProps> {
  render() {
    const { negotiationDetail, showCloseButton } = this.props

    if (isTerminalStatus(negotiationDetail.negotiation.status)) {
      return (
        <TicketFooter>
          <DialogRow horizontalLayout={constants.HorizontalLayout.SpaceBetween}>
            <TextButton
              type={TextButtonType.Tertiary}
              onClick={this.props.onClose || noop}
              dataTestId={'RFQTicketFooter-TextButton-ButtonCloseTicket'}>
              {Locale.translate(Translation.TC_BUTTON_CLOSE_TICKET)}
            </TextButton>
            {!isAuditDetail(negotiationDetail) && canBackToStaging(negotiationDetail) && (
              <TextButton
                type={TextButtonType.Tertiary}
                onClick={this.handleBackToStaging}
                dataTestId={'RFQTicketFooter-TextButton-ActionBackToStaging'}>
                {Locale.translate(Translation.TC_ACTION_BACK_TO_STAGING)}
              </TextButton>
            )}
          </DialogRow>
        </TicketFooter>
      )
    } else {
      return (
        <TicketFooter>
          <DialogRow horizontalLayout={constants.HorizontalLayout.SpaceBetween}>
            <ButtonStrip spacing="5px">
              {showCloseButton && (
                <TextButton
                  type={TextButtonType.Tertiary}
                  onClick={this.props.onClose}
                  dataTestId={'RFQTicketFooter-TextButton-ButtonCloseTicket'}>
                  {Locale.translate(Translation.TC_BUTTON_CLOSE_TICKET)}
                </TextButton>
              )}
              {canCancel(negotiationDetail) && (
                <TextButton
                  type={TextButtonType.Tertiary}
                  onClick={this.handleCancel}
                  dataTestId={'RFQTicketFooter-TextButton-ActionCancelRFQ'}>
                  <ActionIcon
                    icon={iconSet.noEntryIcon}
                    label={Locale.translate(Translation.TC_ACTION_CANCEL_RFQ)}
                    labelPosition={constants.LabelPosition.Right}
                    isCancel={true}
                  />
                </TextButton>
              )}
            </ButtonStrip>
            <ButtonStrip spacing="5px">
              {canReject(negotiationDetail) && (
                <TextButton type={TextButtonType.Bad} onClick={this.handleReject} dataTestId={'RFQTicketFooter-TextButton-ActionReject'}>
                  <ActionIcon
                    icon={iconSet.crossIcon}
                    label={Locale.translate(Translation.TC_ACTION_REJECT)}
                    labelPosition={constants.LabelPosition.Right}
                  />
                </TextButton>
              )}
              {canAccept(negotiationDetail) && (
                <TextButton type={TextButtonType.Good} onClick={this.handleAccept} dataTestId={'RFQTicketFooter-TextButton-ActionAccept'}>
                  <ActionIcon
                    icon={iconSet.tickIcon}
                    label={Locale.translate(Translation.TC_ACTION_ACCEPT)}
                    labelPosition={constants.LabelPosition.Right}
                  />
                </TextButton>
              )}
              {canWithdraw(negotiationDetail) && (
                <TextButton
                  type={TextButtonType.BadOutline}
                  onClick={this.handleWithdraw}
                  dataTestId={'RFQTicketFooter-TextButton-ActionWithdraw'}>
                  {Locale.translate(Translation.TC_ACTION_WITHDRAW)}
                </TextButton>
              )}
              {canSend(negotiationDetail) && (
                <TextButton type={TextButtonType.Good} onClick={this.handleSend} dataTestId={'RFQTicketFooter-TextButton-ActionCounter'}>
                  {negotiationDetail.ownOrder ? (
                    <ActionIcon
                      icon={iconSet.counterIcon}
                      label={Locale.translate(Translation.TC_ACTION_COUNTER)}
                      labelPosition={constants.LabelPosition.Right}
                    />
                  ) : (
                    Locale.translate(Translation.TC_ACTION_SEND_QUOTE)
                  )}
                </TextButton>
              )}
              {canRefresh(negotiationDetail) && (
                <TextButton type={TextButtonType.Good} onClick={this.handleSend} dataTestId={'RFQTicketFooter-TextButton-ActionRefresh'}>
                  {Locale.translate(Translation.TC_ACTION_REFRESH)}
                </TextButton>
              )}
            </ButtonStrip>
          </DialogRow>
        </TicketFooter>
      )
    }
  }

  private handleCancel = async () => {
    const { negotiationDetail, onCancel } = this.props
    const dialogArgs = {
      type: NotificationType.Confirm,
      title: Translation.TC_CANCEL_RFQ,
      message: Translation.SC_CANCEL_RFQ_CONFIRMATION,
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      cancelButton: Translation.TC_BUTTON_NO,
      defaultButton: Translation.TC_BUTTON_YES
    }
    const dialogResult = await this.props.showDialog(dialogArgs)

    if (dialogResult === Translation.TC_BUTTON_YES) {
      onCancel(negotiationDetail.negotiation.negotiationId, negotiationDetail.ownOrder)
    }
  }

  private handleReject = () => {
    const { negotiationDetail, onReject } = this.props
    onReject(negotiationDetail.negotiation.negotiationId, negotiationDetail.ownOrder)
  }

  private handleAccept = () => {
    const { negotiationDetail, onAccept } = this.props
    onAccept(negotiationDetail.negotiation.negotiationId, negotiationDetail.ownOrder)
  }

  private handleWithdraw = () => {
    const { negotiationDetail, onWithdraw } = this.props
    onWithdraw(negotiationDetail.negotiation.negotiationId, negotiationDetail.ownOrder)
  }

  private handleSend = () => {
    const { negotiationDetail, serverClientTimeOffset, onCounter, onQuote } = this.props

    if (isAuditDetail(negotiationDetail)) {
      return
    } else if (negotiationDetail.ownOrder) {
      onCounter(negotiationDetail.negotiation.negotiationId, negotiationDetail.ownOrder, canRefresh(negotiationDetail))
    } else {
      onQuote(
        negotiationDetail.negotiation.negotiationId,
        negotiationDetail.ownOrder,
        getValidUntilTime(serverClientTimeOffset, negotiationDetail.otwDuration),
        negotiationDetail.otwDuration === 0
      )
    }
  }

  private handleBackToStaging = () => {
    const { negotiationDetail, onBackToStaging } = this.props
    onBackToStaging(negotiationDetail.negotiation.negotiationId, negotiationDetail.ownOrder)
  }
}

export const RFQTicketFooter = withModal(connect(mapStateToProps, mapDispatchToProps)(RFQTicketFooterComponent))
